import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Avatar, Box, Button, Divider, Skeleton, Typography } from "@mui/material";

import { User } from "../../../../types/types";
import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";
import { ServiceProvider } from "../../api/types/topServiceProviders.types";
import { Link } from "react-router-dom";

import { UserVerifiedIcon } from "../../assets/icons/go-service-icons";
import { ServiceProviderDetailsIcon } from "../../assets/icons/service-details-icons";

import { StatusCard, StatusCardLoader } from "../StatusCard";
import { getProfileRoute } from "../../../../routers/routeUtils";

const MAX_LINES = 3;

const ServiceUserDetails = ({
  user,
  isLoading,
  rating,
  reviewsCount,
  completedJobs,
  isCurrentUserRequester,
}: {
  user: User | ServiceProvider;
  isLoading: boolean;
  rating: number;
  reviewsCount: number;
  completedJobs: number;
  isCurrentUserRequester: boolean;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  const { fullName, firstTwoChr, media, club, role, isVerified, cityName, countryName, profileLink } =
    useUserDataFormatter(user);

  useEffect(() => {
    const textElement = textRef.current;
    if (textElement) {
      const lineHeight = parseFloat(window.getComputedStyle(textElement).lineHeight);
      const maxHeight = lineHeight * MAX_LINES;
      setIsClamped(textElement.scrollHeight > maxHeight);
    }
  }, [user?.bio]);

  if (isLoading) {
    return <ServiceProviderDetailsLoader />;
  }

  const about = user?.bio || "";

  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  const statuses = Object.entries({ rating, reviewsCount, completedJobs }).map(([key, value]) => ({
    title: key,
    value,
  }));

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <ServiceProviderDetailsIcon />
        <Typography sx={styles.title}>
          {isCurrentUserRequester ? t("providerDetails") : t("requesterDetails")}
        </Typography>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.userInfoContainer}>
        <Box>
          <Box sx={styles.avatarContainer}>
            <Avatar src={media} sx={styles.avatar}>
              {firstTwoChr}
            </Avatar>
            {isVerified && <UserVerifiedIcon sx={styles.verifiedIcon} />}
          </Box>
        </Box>
        <Box sx={styles.userInfo}>
          <Typography sx={styles.userName} component={Link} to={profileLink}>
            {fullName}
          </Typography>
          <Typography sx={styles.userRole}>
            {role} {club && `• ${club}`} {cityName && `• ${cityName}`} {countryName && `• ${countryName}`}
          </Typography>
        </Box>
      </Box>
      {about && (
        <>
          <Typography
            ref={textRef}
            sx={{
              ...styles.bio,
              ...(isClamped &&
                !isExpanded && {
                  display: "-webkit-box",
                  WebkitLineClamp: MAX_LINES,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
            }}
          >
            {about}
          </Typography>
          {isClamped && (
            <Button onClick={toggleExpansion} variant="text" sx={styles.showMoreButton}>
              {isExpanded ? "Show less" : "Show more"}
            </Button>
          )}
        </>
      )}
      <Box sx={{ display: "flex", gap: "16px" }}>
        {statuses.map((status) => (
          <StatusCard key={status.title} status={status} />
        ))}
      </Box>
    </Box>
  );
};

export default ServiceUserDetails;

const ServiceProviderDetailsLoader = () => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <ServiceProviderDetailsIcon />
        <Typography sx={styles.title}>{t("providerDetails")}</Typography>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.userInfoContainer}>
        <Box>
          <Box sx={styles.avatarContainer}>
            <Skeleton variant="circular" width={64} height={64} />
          </Box>
        </Box>
        <Box sx={styles.userInfo}>
          <Skeleton variant="text" width={200} height={28} />
          <Skeleton variant="text" width={300} height={20} />
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="95%" height={20} />
      </Box>
      <Box sx={{ display: "flex", gap: "16px" }}>
        {[...Array(3)].map((_, index) => (
          <StatusCardLoader key={index} />
        ))}
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  divider: {
    mx: "-24px",
  },
  userInfoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  avatarContainer: {
    position: "relative",
  },
  avatar: {
    width: "64px",
    height: "64px",
  },
  verifiedIcon: {
    position: "absolute",
    bottom: "-6px",
    left: "50%",
    transform: "translateX(-50%)",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  userName: {
    textDecoration: "none",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
  },
  userRole: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  bio: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  emptyBio: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "italic",
    fontWeight: 400,
    lineHeight: "150%",
  },
  showMoreButton: {
    alignSelf: "flex-start",
    marginTop: "-8px",
    padding: "0",
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      background: "none",
    },
  },
  statusCardSkeleton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
} as const;
